import React, { useCallback, useMemo } from 'react';

import { useTranslation } from '@modernloop/i18n';
import { MagicIcon } from '@modernloop/shared/icons';
import { AutocompleteRenderGroupParams, ListSubheader, Stack, Theme, Typography } from '@mui/material';

import useOrgId from 'src/hooks/useOrgId';

import ZoomUserSelect from './ZoomUserSelect';
import { Categories, ZoomUserDataWithCategory, ZoomUserSelectProps } from './ZoomUserSelect/types';

export enum SmartOptions {
  Interviewer = 'interviewer-smart-option',
  Scheduler = 'scheduler-smart-option',
}

type Props<TData extends ZoomUserDataWithCategory, InputValue extends string | string[] | undefined> = Omit<
  ZoomUserSelectProps<TData, InputValue>,
  | 'isAdditionalOptionId'
  | 'renderAdditionalOption'
  | 'renderAdditionalOptionGroup'
  | 'getAdditionalOptionSize'
  | 'getAdditionalOptionLabel'
> & {
  hideSchedulerSmartOption?: true;
  /** Omits the "Interviewers" smart option; used where a single meeting needs a single host. */
  hideInterviewerSmartOption?: boolean;
};

const ZoomUserSelectWithSmartOptions = <
  TData extends ZoomUserDataWithCategory,
  InputValue extends string | string[] | undefined,
>(
  props: Props<TData, InputValue>
) => {
  const { t } = useTranslation();
  const orgId = useOrgId();

  const { hideSchedulerSmartOption, hideInterviewerSmartOption } = props;

  const smartOptions = useMemo(() => {
    const result: ZoomUserDataWithCategory[] = [];

    if (orgId) {
      if (!hideInterviewerSmartOption) {
        result.push({
          id: SmartOptions.Interviewer,
          orgId,
          zoomUserId: SmartOptions.Interviewer,
          email: SmartOptions.Interviewer,
          firstName: t('hiring-dashboard-common:common.select.interviewers'),
          lastName: '',
          category: Categories.AdditionalOptions,
        });
      }

      if (!hideSchedulerSmartOption) {
        result.push({
          id: SmartOptions.Scheduler,
          orgId,
          zoomUserId: SmartOptions.Scheduler,
          email: SmartOptions.Scheduler,
          firstName: t('hiring-dashboard-common:common.select.schedulerLabel'),
          lastName: '',
          category: Categories.AdditionalOptions,
        });
      }
    }

    return result;
  }, [hideSchedulerSmartOption, hideInterviewerSmartOption, orgId, t]);

  const isAdditionalOptionId = useCallback((zoomUserId: string) => {
    return SmartOptions.Interviewer === zoomUserId || SmartOptions.Scheduler === zoomUserId;
  }, []);

  const getAdditionalOptionLabel = useCallback(
    (option: ZoomUserDataWithCategory) => {
      if (option.id === SmartOptions.Interviewer) {
        return t('hiring-dashboard-common:common.select.interviewers');
      }
      if (option.id === SmartOptions.Scheduler) {
        return t('hiring-dashboard-common:common.select.schedulerLabel');
      }
      return '';
    },
    [t]
  );

  const renderAdditionalOption = useCallback(
    (option: ZoomUserDataWithCategory) => {
      let label = option.firstName;
      let subtitle = t('hiring-dashboard-common:common.select.interviewerHostSubtitle');

      if (option.id === SmartOptions.Interviewer) {
        label = t('hiring-dashboard-common:common.select.interviewers');
        subtitle = t('hiring-dashboard-common:common.select.interviewersSubtitle');
      } else if (option.id === SmartOptions.Scheduler) {
        label = t('hiring-dashboard-common:common.select.schedulerLabel');
        subtitle = '';
      }

      return (
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="nowrap" data-testid={option.id}>
          <MagicIcon color="primary" />
          <Stack direction="column" flexWrap="nowrap">
            <Typography>{label}</Typography>
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          </Stack>
        </Stack>
      );
    },
    [t]
  );

  const renderAdditionalOptionGroup = useCallback(
    (params: AutocompleteRenderGroupParams) => {
      const group: React.ReactNode[] = [];

      if (params.group) {
        group.push(
          <ListSubheader
            disableSticky
            key={params.key}
            component="div"
            sx={{
              color: (theme: Theme) => theme.palette.primary.main,
              fontWeight: (theme: Theme) => theme.typography.fontWeightMedium,
              padding: (theme: Theme) => `${theme.spacing(1)} ${theme.spacing(0.5)}`,
            }}
          >
            <Typography variant="subtitle2" color="primary">
              {t('hiring-dashboard-common:common.select.smartOptions')}
            </Typography>
          </ListSubheader>
        );
      }
      group.push(params.children);

      return group;
    },
    [t]
  );

  const getAdditionalOptionSize = useCallback(() => {
    return 48;
  }, []);

  return (
    <ZoomUserSelect
      {...props}
      additionalOptions={smartOptions}
      isAdditionalOptionId={isAdditionalOptionId}
      renderAdditionalOption={renderAdditionalOption}
      renderAdditionalOptionGroup={renderAdditionalOptionGroup}
      getAdditionalOptionSize={getAdditionalOptionSize}
      getAdditionalOptionLabel={getAdditionalOptionLabel}
    />
  );
};

export default ZoomUserSelectWithSmartOptions;
